import { SxProps, Theme } from '@mui/material';

/**
 * Shared styles for chat-related components
 */

// Dialog action button style (transparent background)
export const dialogButtonSx: SxProps<Theme> = {
  border: 'none',
  borderRadius: '4px',
  backgroundColor: 'transparent',
  '&:hover': { backgroundColor: 'transparent', border: 'none' },
};

// Timestamp caption style used in chat messages
export const timestampCaptionSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.75rem',
  marginTop: '4px',
};

// User message bubble style
export const userMessageBubbleSx: SxProps<Theme> = {
  display: 'inline-block',
  backgroundColor: 'rgba(8, 30, 67, 0.08)',
  borderRadius: '16px',
  padding: '8px 16px',
};

// Message container padding
export const messageContainerSx: SxProps<Theme> = {
  padding: '0px 0px 32px 0px',
};

// Tooltip slotProps for conversation list (with theme-aware colors)
export const getTooltipSlotProps = (theme: Theme, popperContainerEl?: HTMLElement | null) => ({
  tooltip: {
    sx: {
      maxWidth: '218px',
      textAlign: 'center',
      borderRadius: '4px',
      backgroundColor: theme.palette.grey[700],
      color: theme.palette.common.white,
      fontSize: '10px',
      fontWeight: 500,
      lineHeight: '14px',
      padding: '4px 8px',
      pointerEvents: 'none',
      position: 'relative',
      left: '11px',
    },
  },
  arrow: {
    sx: {
      color: theme.palette.grey[700],
    },
  },
  popper: {
    sx: {
      pointerEvents: 'none',
    },
    disablePortal: Boolean(popperContainerEl),
    container: popperContainerEl ?? undefined,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -4],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          boundary: 'clippingParents',
          padding: 8,
        },
      },
    ],
  },
});

// Menu item style for conversation context menu
export const menuItemSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.text.primary,
  fontSize: '14px',
  py: 1,
});

// Menu icon style
export const menuIconSx = (theme: Theme): SxProps<Theme> => ({
  mr: 1,
  fontSize: '18px',
  color: theme.palette.text.secondary,
});

// Scrollbar styles for conversation list
export const scrollbarSx: SxProps<Theme> = {
  overflowY: 'auto',
  overflowX: 'hidden',
  // Hide scrollbar by default, show on hover
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'transparent',
    borderRadius: '4px',
  },
  '&:hover::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
  },
  // Firefox scrollbar
  scrollbarWidth: 'thin',
  scrollbarColor: 'transparent transparent',
  '&:hover': {
    scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
  },
};

// Conversation item container style
export const getConversationItemSx = (
  theme: Theme,
  isSelected: boolean,
  isMenuOpen: boolean,
  hasConversationId: boolean,
): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  backgroundColor: isSelected ? theme.palette.action.selected : isMenuOpen ? theme.palette.action.hover : 'transparent',
  borderRadius: '4px',
  height: '45px',
  padding: '8px 16px',
  paddingRight: hasConversationId ? '8px' : '16px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: isSelected ? theme.palette.action.selected : theme.palette.action.hover,
  },
  '&:hover .menu-button, & .menu-button.menu-open': {
    opacity: 1,
  },
});

// Truncated text style
export const truncatedTextSx: SxProps<Theme> = {
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  display: 'inline-block',
  textAlign: 'left',
  flex: 1,
  minWidth: 0,
};
